import { Box, IconButton, TextField } from "@material-ui/core"
import MyLocation from "@material-ui/icons/MyLocation"
import Search from "@material-ui/icons/Search"
import { Alert } from "@material-ui/lab"
import { latLng } from "leaflet"
import type { LatLng } from "leaflet"
import { useEffect, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"

import { Trans, useTranslation } from "react-i18next"
import { connect } from "react-redux"
import type {
  GoogleLocationCandidate,
  GoogleLocationLocation,
  GoogleLocationResponse,
  MapDetailDto
} from "../../../models"
import type { AppState, AppThunkDispatch, TopographyActions } from "../../../redux"
import { topographyFetchApiCallCount, topographySearchLocation } from "../../../redux"
import { convertToLatLng } from "../../../utils"
import { LeafletIconPin } from "../../atoms/leaflet-icon-pin/leaflet-icon-pin.component"
import { DialogWindow, DialogWindowFooter, DialogWindowHeader } from "../../molecules"
import { MapViewer } from "../map-viewer/map-viewer.component"

import "./google-maps-location-selector.styles.scss"

export interface IGoogleMapsLocationSelectorProps {
  searchValue: string
  open: boolean
  map: MapDetailDto
  onClose: () => void
  onSelect: (latlng: LatLng) => void
}

export interface IGoogleMapsLocationSelectorStateProps {
  apiCallCount?: number
  searchResults?: GoogleLocationResponse
  updateCount: () => void
  search: (searchValue: string) => void
}

function GoogleMapsLocationSelectorView(
  props: IGoogleMapsLocationSelectorProps & IGoogleMapsLocationSelectorStateProps
): ReactElement {
  const { searchValue, apiCallCount, searchResults, map, open, onClose, onSelect, updateCount, search } = props

  const { t } = useTranslation("contentManager")

  const [selectedLocationIndex, setSelectedLocationIndex] = useState<number>()
  const [searchValueState, setSearchValueState] = useState<string>("")

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (open) {
      updateCount()
    }
  }, [open])

  useEffect(() => {
    setSelectedLocationIndex(undefined)
    setSearchValueState(searchValue)
  }, [searchValue])

  useEffect(() => {
    if (!searchResults) return
    if (searchResults.candidates && searchResults.candidates.length === 1) {
      selectLocation(0)()
    }
  }, [searchResults])

  function confirmLocation(): void {
    if (!searchResults || !searchResults.candidates || selectedLocationIndex === undefined) {
      return
    }
    const googleLocation: GoogleLocationLocation = searchResults.candidates[selectedLocationIndex].geometry!.location!
    const position: LatLng = googleLocationToLatLng(googleLocation)
    onSelect(position)
  }

  function performSearch(): void {
    search(searchValueState)
  }

  function googleLocationToLatLng(location: GoogleLocationLocation): LatLng {
    return latLng(location.lat, location.lng)
  }

  function selectLocation(index: number): () => void {
    return () => {
      setSelectedLocationIndex(index)
    }
  }

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>): void {
    const { value } = event.target
    setSearchValueState(value)
  }

  return (
    <DialogWindow open={open} className="google-maps-location-selector">
      <DialogWindowHeader onClose={onClose} title={t("topography.googleMaps.findLocation")} />

      <Box className="google-maps-location-selector-controls">
        <Alert severity="warning" className="use-alert">
          <Trans
            i18nKey={"contentManager:topography.googleMaps.warningText"}
            values={{ apiCallCount: apiCallCount || 0 }}
            components={{ bold: <strong /> }}
            defaults="Beware this functionality can cost Infinitas money if used lavishly (From ~10,000 retrievals per month). There have already been <bold>{{apiCallCount}}</bold> queries this month."
          />
        </Alert>
        <TextField
          fullWidth
          autoFocus
          variant="outlined"
          placeholder="Zoekterm ..."
          value={searchValueState}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <IconButton onClick={performSearch} color="primary">
                <Search />
              </IconButton>
            )
          }}
        />
        <Alert severity="info" className="manual-info">
          {t("topography.googleMaps.searchSpecifiedLocation")}
        </Alert>
      </Box>

      <Box className="google-maps-location-selector-map-container">
        <MapViewer
          mapId={map.id}
          casperId={map.casperId!}
          corner={convertToLatLng(map.corner)}
          oppositeCorner={convertToLatLng(map.oppositeCorner)}
        >
          {searchResults?.candidates?.map((candidate: GoogleLocationCandidate, index: number) => (
            <LeafletIconPin
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO in a later PR
              key={index}
              position={googleLocationToLatLng(candidate.geometry!.location!)}
              icon={<MyLocation />}
              onClick={selectLocation(index)}
              className={index === selectedLocationIndex ? "selected" : ""}
            />
          ))}
        </MapViewer>
      </Box>
      <DialogWindowFooter
        disableConfirm={selectedLocationIndex === undefined}
        onCancel={onClose}
        onConfirm={confirmLocation}
      />
    </DialogWindow>
  )
}

function mapStateToProps(state: AppState) {
  return {
    apiCallCount: state.topography.lookupCount,
    searchResults: state.topography.locationResults
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<TopographyActions>) {
  return {
    updateCount: () => dispatch(topographyFetchApiCallCount()),
    search: (searchValue: string) => dispatch(topographySearchLocation(searchValue))
  }
}

export const GoogleMapsLocationSelector = connect(mapStateToProps, mapDispatchToProps)(GoogleMapsLocationSelectorView)
